import { CheckOutlined, EditOutlined, EyeOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Col, DatePicker, Input, Row, Select, Space, Table, Tag, Tooltip } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { ContentHeader } from "../../components";
import { responseStatus } from "../../constants";
import { menuHelper } from "../../helpers/account";
import { Links } from "../../helpers/Links";
import { ThanhLyTaiSan, TrangThaiThanhLyTaiSan } from "../../models/taisan/ThanhLyTaiSan";
import { hoanThanhThanhLy, timKiemDSPhieuThanhLy } from "../../services/thanhlytaisan";

const { RangePicker } = DatePicker;

const DanhSachThanhLyTaiSan = () => {
    const navigate = useNavigate();
    const menu: any = menuHelper.checkMultiPermission([Links.ThanhLyTaiSan.ChiTiet, Links.ThanhLyTaiSan.Xem]);
    const [loading, setLoading] = useState(false);
    const [dsPhieu, setDsPhieu] = useState<ThanhLyTaiSan[]>([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [tuKhoa, setTuKhoa] = useState("");
    const [trangThai, setTrangThai] = useState<number | undefined>();
    const [tuNgay, setTuNgay] = useState<Date | undefined>();
    const [denNgay, setDenNgay] = useState<Date | undefined>();

    const trangThaiOptions = [
        { value: TrangThaiThanhLyTaiSan.DuThao, label: "Dự thảo" },
        { value: TrangThaiThanhLyTaiSan.HoanThanh, label: "Hoàn thành" },
    ];

    const getTrangThaiTag = (value: number) => {
        switch (value) {
            case TrangThaiThanhLyTaiSan.DuThao:
                return <Tag>Dự thảo</Tag>;
            case TrangThaiThanhLyTaiSan.HoanThanh:
                return <Tag color="green">Hoàn thành</Tag>;
            default:
                return <Tag>Không xác định</Tag>;
        }
    };

    const loadDanhSach = async () => {
        try {
            setLoading(true);
            const result = await timKiemDSPhieuThanhLy({
                tuKhoa,
                trangThai,
                tuNgay,
                denNgay,
            }, (currentPage - 1) * pageSize, pageSize);

            setDsPhieu(result.data);
            setTotal(result.recordsTotal);
        } catch (error: any) {
            toast.error("Lỗi khi tải danh sách phiếu thanh lý: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadDanhSach();
    }, [currentPage, pageSize]);

    const handleTimKiem = () => {
        setCurrentPage(1);
        loadDanhSach();
    };




    const handleHoanThanh = async (id: number) => {
        try {
            setLoading(true);
            const response = await hoanThanhThanhLy(id);
            if (response.trangThai !== responseStatus.thanhCong) {
                throw new Error(response.thongBao);
            }
            toast.success("Hoàn thành thanh lý thành công");
            loadDanhSach();
        } catch (error: any) {
            toast.error("Lỗi khi hoàn thành thanh lý: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: "STT",
            key: "stt",
            width: 60,
            render: (_: any, __: any, index: number) => (currentPage - 1) * pageSize + index + 1,
        },
        {
            title: "Ngày lập",
            dataIndex: "ngayLap",
            key: "ngayLap",
            width: 120,
            render: (value: Date) => moment(value).format("DD/MM/YYYY"),
        },
        {
            title: "Nội dung",
            dataIndex: "noiDung",
            key: "noiDung",
            render: (value: string) => value ? value.replace(/<[^>]+>/g, "").slice(0, 120) : "—",
        },
        {
            title: "Số tài sản",
            dataIndex: "soLuongTaiSan",
            key: "soLuongTaiSan",
            width: 100,
            align: "center" as const,
        },
        {
            title: "Số file",
            dataIndex: "soLuongTaiLieu",
            key: "soLuongTaiLieu",
            width: 90,
            align: "center" as const,
        },
        {
            title: "Người tạo",
            dataIndex: "nguoiTao",
            key: "nguoiTao",
            width: 160,
        },
        {
            title: "Trạng thái",
            dataIndex: "trangThai",
            key: "trangThai",
            width: 130,
            render: (value: number) => getTrangThaiTag(value),
        },
        {
            title: "Thao tác",
            key: "action",
            width: 280,
            fixed: "right" as const,
            render: (_: any, record: ThanhLyTaiSan) => (
                <Space wrap>
                    {menu[Links.ThanhLyTaiSan.Xem] && (
                        <Tooltip title="Xem chi tiết">
                            <Link to={`${Links.ThanhLyTaiSan.Xem}?id=${record.id}`} className="hrm-btn hrm-btn-sm btn-success">
                                Xem
                            </Link>
                        </Tooltip>
                    )}
                    {menu[Links.ThanhLyTaiSan.ChiTiet] && record.trangThai !== TrangThaiThanhLyTaiSan.HoanThanh && (
                        <Button
                            type="primary"
                            size="small"
                            className="hrm-btn hrm-btn-warning"
                            icon={<EditOutlined />}
                            onClick={() => navigate(`${Links.ThanhLyTaiSan.ChiTiet}?id=${record.id}`)}
                        >
                            Sửa
                        </Button>
                    )}
                    {menu[Links.ThanhLyTaiSan.ChiTiet] && record.trangThai !== TrangThaiThanhLyTaiSan.HoanThanh && (
                        <Button
                            type="primary"
                            size="small"
                            disabled={(record.soLuongTaiLieu || 0) <= 0}
                            onClick={() => handleHoanThanh(record.id || 0)}
                        >
                            Hoàn thành
                        </Button>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div>
            <ContentHeader title="Danh mục thanh lý tài sản" />
            <section className="content">
                <div className="container-fluid">
                    <div className="card">
                        <div className="card-body">
                            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                                <Col xs={24} sm={12} md={8}>
                                    <Input
                                        placeholder="Tìm kiếm..."
                                        value={tuKhoa}
                                        onChange={(e) => setTuKhoa(e.target.value)}
                                        onPressEnter={handleTimKiem}
                                        prefix={<SearchOutlined />}
                                    />
                                </Col>
                                <Col xs={24} sm={12} md={4}>
                                    <Select
                                        placeholder="Trạng thái"
                                        value={trangThai}
                                        onChange={setTrangThai}
                                        allowClear
                                        style={{ width: "100%" }}
                                        options={trangThaiOptions}
                                    />
                                </Col>
                                <Col xs={24} sm={24} md={6}>
                                    <RangePicker
                                        style={{ width: "100%" }}
                                        format="DD/MM/YYYY"
                                        placeholder={["Từ ngày", "Đến ngày"]}
                                        onChange={(dates) => {
                                            if (dates) {
                                                setTuNgay(dates[0]?.toDate());
                                                setDenNgay(dates[1]?.toDate());
                                            } else {
                                                setTuNgay(undefined);
                                                setDenNgay(undefined);
                                            }
                                        }}
                                    />
                                </Col>
                                <Col xs={24} sm={24} md={6}>
                                    <Space>
                                        <Button type="primary" icon={<SearchOutlined />} onClick={handleTimKiem}>
                                            Tìm kiếm
                                        </Button>
                                        {menu[Links.ThanhLyTaiSan.ChiTiet] && (
                                            <Button type="primary" className="hrm-btn hrm-btn-warning" icon={<PlusOutlined />} onClick={() => navigate(Links.ThanhLyTaiSan.ChiTiet)}>
                                                Thêm mới
                                            </Button>
                                        )}
                                    </Space>
                                </Col>
                            </Row>

                            <Table
                                columns={columns}
                                dataSource={dsPhieu}
                                loading={loading}
                                rowKey="id"
                                scroll={{ x: 1400 }}
                                pagination={{
                                    current: currentPage,
                                    pageSize,
                                    total,
                                    showSizeChanger: true,
                                    showTotal: (value) => `Tổng số: ${value} bản ghi`,
                                    onChange: (page, nextPageSize) => {
                                        setCurrentPage(page);
                                        setPageSize(nextPageSize);
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default DanhSachThanhLyTaiSan;
